/**
 * 游戏主屏幕 - 解耦重构版
 * 
 * 遵循单一职责原则，将各功能拆分到独立组件：
 * - panels/: 面板组件 (TopBar, ChatArea, BottomPanel)
 * - modals/: 弹窗组件 (SwapCards, DiscardCards, TargetSelect)
 * - messages/: 消息组件
 */

import React from 'react';
import type { GameCard } from '../types';
import { useGameLoop } from '../hooks/useGameLoop';
import { useCardSelection } from '../hooks/game/useCardSelection';
import { MAX_HAND_SIZE } from '../constants';

import {
  TopBar,
  ChatArea,
  BottomPanel,
  SwapCardsModal,
  DiscardCardsModal,
  TargetSelectModal,
} from './game';

const MAX_SWAP_SELECT = 5;

interface GameScreenProps {
  onSettingsClick?: () => void;
}

const GameScreen: React.FC<GameScreenProps> = ({ onSettingsClick }) => {
  // 核心游戏逻辑
  const {
    charA, charB,
    turn, cpA, cpB, messages, cards, deck, swapCount, pendingCards, phase, loadingText,
    selectedCard, setSelectedCard, handleNextTurn,
    handleSwap, handleSwapComplete, handleSwapCancel,
    handleDiscardComplete,
    isTyping, typingSpeaker,
  } = useGameLoop();

  // 卡牌选择逻辑
  const {
    selectedSwapIds,
    selectedSwapCount,
    canConfirmSwap,
    toggleSwapCard,
    getSelectedSwapIds,
    resetSwapSelection,
    selectedDiscardIds,
    selectedDiscardCount,
    toggleDiscardCard,
    getSelectedDiscardIds,
    resetDiscardSelection,
  } = useCardSelection({ maxSwapSelect: MAX_SWAP_SELECT });

  // 弃牌计算: 手牌超出上限的数量
  const needToDiscard = Math.max(0, cards.length - MAX_HAND_SIZE);
  const canConfirmDiscard = needToDiscard > 0 && selectedDiscardCount === needToDiscard;

  // 换牌时只显示牌堆中不在手牌里的卡牌
  const swappableDeckCards = deck.filter(d => !cards.some(c => c.id === d.id));

  // 处理 Swap 确认
  const onConfirmSwap = () => {
    const ids = getSelectedSwapIds();
    if (ids.length > 0) {
      handleSwapComplete(ids);
      resetSwapSelection();
    }
  };

  // 处理 Swap 取消
  const onCancelSwap = () => {
    handleSwapCancel();
    resetSwapSelection();
  };

  // 处理 Discard 确认
  const onConfirmDiscard = () => {
    if (canConfirmDiscard) {
      handleDiscardComplete(getSelectedDiscardIds());
      resetDiscardSelection();
    }
  };

  // 处理目标选择
  const onSelectTarget = (card: GameCard, target: 'A' | 'B') => {
    handleNextTurn(card, target);
  };

  // 处理跳过回合
  const onSkipTurn = () => {
    handleNextTurn();
  };

  return (
    <div className="flex flex-col h-screen bg-[#FFF8F0] relative">
      {/* 顶部栏 */}
      <TopBar charA={charA} charB={charB} turn={turn} cpA={cpA} cpB={cpB} onSettingsClick={onSettingsClick} />

      {/* 聊天区域 */}
      <ChatArea
        messages={messages}
        charA={charA}
        charB={charB}
        isTyping={isTyping}
        typingSpeaker={typingSpeaker}
        loadingText={loadingText}
      />

      {/* 底部面板 */}
      <BottomPanel
        cards={cards}
        deck={deck}
        phase={phase}
        swapCount={swapCount}
        onSkip={onSkipTurn}
        onSwap={handleSwap}
        onSelectCard={setSelectedCard}
      />

      {/* 换牌弹窗 */}
      <SwapCardsModal
        isOpen={phase === 'SWAP_CARDS'}
        deckCards={swappableDeckCards}
        handSize={cards.length}
        swapCount={swapCount}
        selectedIds={selectedSwapIds}
        selectedCount={selectedSwapCount}
        canConfirm={canConfirmSwap}
        onToggleCard={toggleSwapCard}
        onConfirm={onConfirmSwap}
        onCancel={onCancelSwap}
      />

      {/* 弃牌弹窗 */}
      <DiscardCardsModal
        handCards={cards}
        needToDiscard={needToDiscard}
        selectedIds={selectedDiscardIds}
        selectedCount={selectedDiscardCount}
        canConfirm={canConfirmDiscard}
        onToggleCard={(card) => toggleDiscardCard(card, needToDiscard)}
        onConfirm={onConfirmDiscard}
      />

      {/* 目标选择弹窗 */}
      <TargetSelectModal
        selectedCard={selectedCard}
        charA={charA}
        charB={charB}
        onSelectTarget={onSelectTarget}
        onCancel={() => setSelectedCard(null)}
      />
    </div>
  );
};

export default GameScreen;
